import { loadStripe } from '@stripe/stripe-js';
import STRIPE_CONFIG, { isStripeConfigured } from '../config/stripe';

// Initialize Stripe with your publishable key
let stripePromise = null;
let currentKey = null;

const getStripe = () => {
  const publishableKey = STRIPE_CONFIG.publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  
  if (!publishableKey) {
    console.error('No Stripe publishable key found!');
  }
  
  // Re-initialize if the key has changed (useful when switching between test/live)
  if (currentKey !== publishableKey) {
    stripePromise = null;
    currentKey = publishableKey;
  }
  
  if (!stripePromise) {
    if (isStripeConfigured()) {
      stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
    } else {
      console.warn('Stripe not properly configured. Please check your REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable.');
    }
  }
  
  return stripePromise;
};

// Create a payment intent on your backend
const createPaymentIntent = async (amount, productId, customerEmail) => {
  try {
    // This would typically call your backend API
    // For now, we'll return a mock response structure
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        productId,
        customerEmail,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

// Process payment with Stripe
const processPayment = async (paymentMethodId, amount, productId, customerEmail) => {
  try {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe not initialized');
    }

    // Create payment intent first
    const { clientSecret } = await createPaymentIntent(amount, productId, customerEmail);

    // Confirm the payment
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId,
    });

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      paymentIntent,
      transactionId: paymentIntent.id,
    };
  } catch (error) {
    console.error('Payment processing error:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Validate payment form data
const validatePaymentForm = (formData) => {
  const errors = [];

  if (!formData.email || !formData.email.trim()) {
    errors.push('Email is required');
  }

  if (!formData.name || !formData.name.trim()) {
    errors.push('Full name is required');
  }

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (formData.email && !emailRegex.test(formData.email)) {
    errors.push('Please enter a valid email address');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Format amount for display (convert cents to dollars)
const formatAmount = (amountInCents) => {
  return (amountInCents / 100).toFixed(2);
};

// Format amount for Stripe (convert dollars to cents)
const formatAmountForStripe = (amountInDollars) => {
  return Math.round(parseFloat(amountInDollars) * 100);
};

export {
  getStripe,
  createPaymentIntent,
  processPayment,
  validatePaymentForm,
  formatAmount,
  formatAmountForStripe,
};
